// A standalone render of the Timeline's platform-map matrix (context ×
// business-function coverage grid) from a single `TimelineSnapshot`.
//
// Reuses every computation + cell helper the live Timeline uses
// (data-views.ts / matrix-cells-views.ts / MatrixCell / GroupedCompLabel), so
// the visual is identical. Scoped to the grouped presentation (one row per
// derivedContext) — the snapshot-to-snapshot diff markers and the flat/skill view
// live in TimelineView and aren't needed for a point-in-time snapshot. Built
// for the standalone /narrative-test page; TimelineView is left untouched.

import type { Skill } from '@lib/api-types/derived-contexts';
import type { TimelineSnapshot } from '@lib/api-types/views';
import { useMemo } from 'react';

import {
  NO_EXPANDED,
  TALENT_LEVELS,
  buildDerivedContextFunctionGate,
  buildRatingIndex,
  buildSkillFunctions,
  compRowHeight,
  indexSkills,
  isCompactCatalog,
  isInstitutional,
  remapSkillFunctionsToParents,
  rollupColumns,
  type DerivedContextFunctionGate,
  type DerivedContext,
  type Engineer,
  type RatingValue,
  type RollupColumn,
} from '../data-views.ts';
import { sortByClientDistance } from '../derived-context-order.ts';
import { makeMergedCoverageCell } from '../matrix-cells-views.ts';

import { MatrixCell } from './MatrixCell.tsx';
import { ColumnHeader, GroupedCompLabel, TruncatedLabel } from './TruncatedLabel.tsx';

const showAllSkills = () => true;

interface PlatformMapMatrixProps {
  snapshot: TimelineSnapshot;
  /** Coverage threshold — an engineer "covers" a cell at ≥ this rating. */
  talentLevel?: RatingValue;
  /** Skill visibility predicate (e.g. the "Show Libraries" toggle). */
  techVisible?: (skill: Skill) => boolean;
}

/** Renders the grouped derivedContext × business-function matrix for one snapshot.
 *  Returns null when there's nothing to show (caller renders the empty state). */
export function PlatformMapMatrix({
  snapshot,
  talentLevel = 2,
  techVisible = showAllSkills,
}: PlatformMapMatrixProps) {
  const fns = snapshot.business_functions;
  const ratings = snapshot.ratings;
  // Only humans populate coverage (agents aren't "contributors").
  const team = useMemo(
    () => snapshot.engineers.filter((e) => e.kind === 'human'),
    [snapshot.engineers],
  );

  const visibleComps = useMemo(
    () =>
      sortByClientDistance(snapshot.derivedContexts)
        .map((comp) => ({ ...comp, skills: comp.skills.filter(techVisible) }))
        .filter((comp) => comp.skills.length > 0),
    [snapshot.derivedContexts, techVisible],
  );

  const skillsById = useMemo(() => indexSkills(visibleComps), [visibleComps]);
  const skillFunctions = useMemo(() => buildSkillFunctions(visibleComps), [visibleComps]);
  const ratingIndex = useMemo(
    () => buildRatingIndex(skillsById, team, ratings),
    [skillsById, team, ratings],
  );
  const columns = useMemo<RollupColumn[]>(
    () => rollupColumns(fns, NO_EXPANDED),
    [fns],
  );
  const skillFunctionsGrouped = useMemo(
    () => remapSkillFunctionsToParents(skillFunctions, fns),
    [skillFunctions, fns],
  );
  const cfGate = useMemo(
    () => buildDerivedContextFunctionGate(visibleComps, fns),
    [visibleComps, fns],
  );
  const levelLabel = TALENT_LEVELS.find((l) => l.key === talentLevel)?.label ?? '';

  const compact = useMemo(() => isCompactCatalog(snapshot.derivedContexts), [snapshot.derivedContexts]);
  const gridStyle = useMemo(() => {
    const heights = visibleComps.map((c) => compRowHeight(c.skills.length, compact) + 'px').join(' ');
    return {
      gridTemplateColumns: `130px 42px 6px 140px repeat(${columns.length}, minmax(0, 1fr))`,
      gridTemplateRows: `auto ${heights}`,
    } as Record<string, string>;
  }, [columns.length, visibleComps, compact]);

  if (visibleComps.length === 0 || columns.length === 0) return null;

  return (
    <div className='snap-grid' style={gridStyle}>
      {/* Four leading label columns (derivedContext / connector / prop / skill). */}
      <div className='snap-grid__col-header' />
      <div className='snap-grid__col-header' />
      <div className='snap-grid__col-header' />
      <div className='snap-grid__col-header' />
      {columns.map((col) => (
        <ColumnHeader key={col.fn.id} display={col.fn.display} subtitle={col.fn.subtitle} />
      ))}
      {visibleComps.map((comp) => (
        <GroupedRow
          key={comp.id}
          comp={comp}
          columns={columns}
          threshold={talentLevel}
          levelLabel={levelLabel}
          ratingIndex={ratingIndex}
          skillFunctions={skillFunctions}
          skillFunctionsGrouped={skillFunctionsGrouped}
          cfGate={cfGate}
          team={team}
        />
      ))}
    </div>
  );
}

/** Adjacency map a column's cells use: grouped (child→parent) for a roll-up
 *  group column, otherwise the flat map. */
function mapForColumn(
  col: RollupColumn,
  flat: ReturnType<typeof buildSkillFunctions>,
  grouped: ReturnType<typeof buildSkillFunctions>,
): ReturnType<typeof buildSkillFunctions> {
  return col.kind === 'group' ? grouped : flat;
}

interface GroupedRowProps {
  comp: DerivedContext;
  columns: RollupColumn[];
  threshold: RatingValue;
  levelLabel: string;
  ratingIndex: ReturnType<typeof buildRatingIndex>;
  skillFunctions: ReturnType<typeof buildSkillFunctions>;
  skillFunctionsGrouped: ReturnType<typeof buildSkillFunctions>;
  cfGate: DerivedContextFunctionGate;
  team: Engineer[];
}

/** One row per derivedContext — merged coverage across the derivedContext's skills. */
function GroupedRow({
  comp,
  columns,
  threshold,
  levelLabel,
  ratingIndex,
  skillFunctions,
  skillFunctionsGrouped,
  cfGate,
  team,
}: GroupedRowProps) {
  return (
    <>
      <GroupedCompLabel
        propCount={comp.skills.filter(isInstitutional).length}
        name={<TruncatedLabel text={comp.display} max={22} />}
        count={
          <span>
            {comp.skills.length} skill{comp.skills.length === 1 ? '' : 's'}
          </span>
        }
      />
      {columns.map((col) => {
        // Shared platform-map gate: only a genuinely-served (comp × fn) cell
        // renders coverage; others stay gray.
        const cell = cfGate.serves(comp.id, col)
          ? makeMergedCoverageCell(
              comp,
              col.fn,
              threshold,
              levelLabel,
              ratingIndex,
              mapForColumn(col, skillFunctions, skillFunctionsGrouped),
              team,
            )
          : { value: null, levelClass: 'na' as const, tooltip: null };
        return <MatrixCell key={col.fn.id} {...cell} extraClass='is-merged' />;
      })}
    </>
  );
}
